/**
 * Benutzerverwaltung - mit wiederverwendbarem Listing Pattern
 * 
 * Features:
 * - URL-persistierte Filter, Suche, Sortierung
 * - Volltext-Suche über alle relevanten Felder
 * - Multi-Select Filter (Rollen)
 * - TriState Filter (Chipnr., RFID)
 * - Klickbare Spaltenheader für Sortierung
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, RefreshCw, X, CreditCard, UserCheck, UserX, Upload, ChevronRight } from 'lucide-react';
import { CsvDropzone, ImportField, FieldMapping, ImportResult } from '../../components/CsvDropzone';
import { AdminLayout } from '../../components/AdminLayout';
import { Tooltip } from '../../components/Tooltip';
import { useAuth } from '../../App';
import {
  useListQueryState,
  applyListState,
  matchesTriState,
  compareString,
  compareBoolean,
  comparePresence,
  compareSmartNumeric,
  SearchInput,
  StatusSelect,
  TriStateSelect,
  MultiSelectDropdown,
  ResetButton,
  SortableHeader,
  FilterBar,
  type ListConfig,
  type UrlParamConfig,
  type TriStateFilter,
} from '../../lib/listing';

interface User {
  id: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  externalEmployeeId: string | null;
  role: UserRole;
  isActive: boolean;
  twoFactorEnabled: boolean;
  nfcChipId: string | null;
  rfidPrintedCode: string | null;
}

type UserRole = 'WORKER' | 'ADMIN' | 'SUPERADMIN' | 'SYSTEM';

const ROLE_LABELS: Record<UserRole, string> = {
  WORKER: 'Mitarbeiter',
  ADMIN: 'Admin',
  SUPERADMIN: 'Superadmin',
  SYSTEM: 'System',
};

const ROLE_COLORS: Record<UserRole, string> = {
  WORKER: 'bg-slate-100 text-slate-800',
  ADMIN: 'bg-blue-100 text-blue-800',
  SUPERADMIN: 'bg-purple-100 text-purple-800',
  SYSTEM: 'bg-orange-100 text-orange-800',
};

interface RfidFormData {
  nfcChipId: string;
  rfidPrintedCode: string;
}

// ─────────────────────────────────────────────────────────────
// Users-spezifische Filter-Typen
// ─────────────────────────────────────────────────────────────

interface UserFilters {
  roles: string[];
  chip: TriStateFilter;
  rfid: TriStateFilter;
}

type UserSortKey = 'name' | 'role' | 'status' | 'chip' | 'rfid';

// URL Param Config
const URL_CONFIG: UrlParamConfig<UserFilters, UserSortKey> = {
  defaults: {
    status: 'active',
    sortKey: 'name',
    sortDir: 'asc',
    filters: {
      roles: [],
      chip: 'all',
      rfid: 'all',
    },
  },
  multiSelectKeys: ['roles'],
  triStateKeys: ['chip', 'rfid'],
};

// Rollen-Optionen für Multi-Select
const ROLE_OPTIONS = [
  { value: 'WORKER', label: 'Mitarbeiter' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'SUPERADMIN', label: 'Superadmin' },
];

// List Config für applyListState
const LIST_CONFIG: ListConfig<User, UserFilters, UserSortKey> = {
  getSearchText: (user) => [
    user.displayName,
    user.firstName || '',
    user.lastName || '',
    // Kombinationen für bessere Suche
    `${user.firstName || ''} ${user.lastName || ''}`,
    `${user.lastName || ''} ${user.firstName || ''}`,
    user.externalEmployeeId || '',
    user.email || '',
    user.rfidPrintedCode || '',
    user.nfcChipId || '',
    ROLE_LABELS[user.role] || '',
  ],
  getStatus: (user) => user.isActive !== false,
  filterDefs: {
    roles: (user, roles) => {
      if (!roles || roles.length === 0) return true;
      return roles.includes(user.role);
    },
    chip: (user, filter) => matchesTriState(user.rfidPrintedCode, filter),
    rfid: (user, filter) => matchesTriState(user.nfcChipId, filter),
  },
  sortDefs: {
    name: (a, b) => compareString(a.lastName || '', b.lastName || '') || compareString(a.firstName || '', b.firstName || ''),
    role: (a, b) => {
      const order = ['WORKER', 'ADMIN', 'SUPERADMIN', 'SYSTEM'];
      return order.indexOf(a.role) - order.indexOf(b.role);
    },
    status: (a, b) => compareBoolean(a.isActive, b.isActive),
    chip: (a, b) => compareSmartNumeric(a.rfidPrintedCode, b.rfidPrintedCode),
    rfid: (a, b) => comparePresence(a.nfcChipId, b.nfcChipId),
  },
  defaultSortKey: 'name',
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  
  // URL-State für Filter/Sort
  const { state: listState, actions } = useListQueryState<UserFilters, UserSortKey>(URL_CONFIG);
  
  // Local search input (für sofortige UI-Reaktion)
  const [searchInput, setSearchInput] = useState(listState.q);
  
  // RFID Dialog State (bleibt als Schnellzugriff in Liste)
  const [rfidDialogOpen, setRfidDialogOpen] = useState(false);
  const [rfidUser, setRfidUser] = useState<User | null>(null);
  const [rfidForm, setRfidForm] = useState<RfidFormData>({ nfcChipId: '', rfidPrintedCode: '' });
  const [savingRfid, setSavingRfid] = useState(false);
  const [rfidError, setRfidError] = useState<string | null>(null);
  
  // Import Dialog State
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Load users
  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/users', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filter + Sort via Listing Pattern
  // Bei exaktem RFID/NFC-Match: Status-Filter ignorieren
  const filtered = useMemo(() => {
    const q = listState.q?.trim().toLowerCase();
    
    // Prüfe ob exakter RFID/NFC-Match existiert
    if (q) {
      const exactMatch = users.find(u => 
        (u.rfidPrintedCode && u.rfidPrintedCode.toLowerCase() === q) ||
        (u.nfcChipId && u.nfcChipId.toLowerCase() === q)
      );
      if (exactMatch) {
        // Exakter Match gefunden → nur diesen User zurückgeben
        return [exactMatch];
      }
    }
    
    // Kein exakter Match → normale Filter-Logik
    return applyListState(users, LIST_CONFIG, listState);
  }, [users, listState]);
  
  // Prüfe ob Filter aktiv sind (für Reset-Button)
  const hasActiveFilters = useMemo(() => {
    return listState.q !== '' ||
      listState.status !== URL_CONFIG.defaults.status ||
      listState.filters.roles.length > 0 ||
      listState.filters.chip !== 'all' ||
      listState.filters.rfid !== 'all' ||
      listState.sortKey !== URL_CONFIG.defaults.sortKey ||
      listState.sortDir !== URL_CONFIG.defaults.sortDir;
  }, [listState]);


  // Open RFID dialog (Schnellzugriff aus Liste)
  const openRfidDialog = (user: User) => {
    setRfidUser(user);
    setRfidForm({
      nfcChipId: user.nfcChipId || '',
      rfidPrintedCode: user.rfidPrintedCode || '',
    });
    setRfidError(null);
    setRfidDialogOpen(true);
  };

  // Save RFID
  const saveRfid = async () => {
    if (!rfidUser) return;
    
    setSavingRfid(true);
    setRfidError(null);
    try {
      const res = await fetch(`/api/admin/users/${rfidUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          nfcChipId: rfidForm.nfcChipId.trim() || null,
          rfidPrintedCode: rfidForm.rfidPrintedCode.trim() || null,
        }),
      });
      
      if (res.ok) {
        setRfidDialogOpen(false);
        loadUsers();
      } else {
        const err = await res.json().catch(() => ({ message: 'Speichern fehlgeschlagen' }));
        setRfidError(err.message || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setRfidError('Netzwerkfehler beim Speichern');
    }
    setSavingRfid(false);
  };

  // Toggle user active status
  const toggleUserStatus = async (user: User) => {
    const newStatus = user.isActive === false;
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: newStatus }),
      });
      
      if (res.ok) {
        loadUsers();
      }
    } catch (err) {
      console.error('Fehler beim Statuswechsel:', err);
    }
  };

  // Import fields definition
  const importFields: ImportField[] = [
    { key: 'externalEmployeeId', label: 'Personalnummer', required: true, description: 'Eindeutige ID aus HR-System' },
    { key: 'displayName', label: 'Anzeigename', required: false },
    { key: 'firstName', label: 'Vorname', required: false, description: 'Mindestens Vorname oder Nachname' },
    { key: 'lastName', label: 'Nachname', required: false, description: 'Mindestens Vorname oder Nachname' },
    { key: 'email', label: 'E-Mail', required: false },
    { key: 'rfidPrintedCode', label: 'Chipnummer', required: false, description: 'Optional, darf leer sein' },
  ];

  // Import handler with dryRun and applyUpdates support
  const handleImport = async (
    data: Record<string, unknown>[], 
    mapping: FieldMapping,
    options?: { dryRun?: boolean; applyUpdates?: boolean }
  ): Promise<ImportResult> => {
    const res = await fetch('/api/admin/users/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ data, mapping, options }),
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Import fehlgeschlagen' }));
      throw new Error(err.error || 'Import fehlgeschlagen');
    }
    
    const result = await res.json();
    
    // Nur bei echtem Import (nicht dryRun) Result setzen und Users neu laden
    if (!options?.dryRun) {
      setImportResult(result);
      loadUsers();
    }
    
    return result;
  };

  // Check if user can import (ADMIN/SUPERADMIN/SYSTEM)
  const canImport = currentUser && ['ADMIN', 'SUPERADMIN', 'SYSTEM'].includes(currentUser.role);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-6 h-6" />
              Benutzer
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {filtered.length} von {users.filter(u => u.isActive !== false).length} Benutzer
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadUsers}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {canImport && (
              <button 
                onClick={() => { setImportResult(null); setImportDialogOpen(true); }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
            )}
            <button 
              onClick={() => navigate('/admin/users/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neuer Benutzer
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <FilterBar>
          <SearchInput
            value={searchInput}
            onChange={(v) => { setSearchInput(v); actions.setQ(v); }}
            onChangeImmediate={(v) => { setSearchInput(v); actions.setQImmediate(v); }}
            placeholder="Suchen..."
            className="flex-1 min-w-[200px]"
          />
          <StatusSelect
            value={listState.status}
            onChange={actions.setStatus}
          />
          <MultiSelectDropdown
            value={listState.filters.roles}
            onChange={(v) => actions.setFilter('roles', v)}
            onToggle={(v) => actions.toggleInMultiFilter('roles', v)}
            options={ROLE_OPTIONS}
            label="Rolle"
          />
          <TriStateSelect
            value={listState.filters.chip}
            onChange={(v) => actions.setFilter('chip', v)}
            label="Chipnr."
            presentLabel="Mit Chipnr."
            missingLabel="Ohne Chipnr."
          />
          <TriStateSelect
            value={listState.filters.rfid}
            onChange={(v) => actions.setFilter('rfid', v)}
            label="RFID"
            presentLabel="Mit RFID"
            missingLabel="Ohne RFID"
          />
          <ResetButton
            onClick={actions.reset}
            hasActiveFilters={hasActiveFilters}
          />
        </FilterBar>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {hasActiveFilters ? 'Keine Treffer' : 'Keine Benutzer vorhanden'}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <SortableHeader
                    sortKey="name"
                    currentSortKey={listState.sortKey}
                    currentSortDir={listState.sortDir}
                    onSort={(k) => actions.setSort(k as UserSortKey)}
                  >
                    Nachname
                  </SortableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Vorname</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">E-Mail</th>
                  <SortableHeader
                    sortKey="role"
                    currentSortKey={listState.sortKey}
                    currentSortDir={listState.sortDir}
                    onSort={(k) => actions.setSort(k as UserSortKey)}
                  >
                    Rolle
                  </SortableHeader>
                  <SortableHeader
                    sortKey="status"
                    currentSortKey={listState.sortKey}
                    currentSortDir={listState.sortDir}
                    onSort={(k) => actions.setSort(k as UserSortKey)}
                    className="hidden lg:table-cell"
                  >
                    Status
                  </SortableHeader>
                  <SortableHeader
                    sortKey="chip"
                    currentSortKey={listState.sortKey}
                    currentSortDir={listState.sortDir}
                    onSort={(k) => actions.setSort(k as UserSortKey)}
                    className="hidden md:table-cell"
                  >
                    Chipnr.
                  </SortableHeader>
                  <SortableHeader
                    sortKey="rfid"
                    currentSortKey={listState.sortKey}
                    currentSortDir={listState.sortDir}
                    onSort={(k) => actions.setSort(k as UserSortKey)}
                    className="hidden md:table-cell"
                  >
                    RFID
                  </SortableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(u => (
                  <tr 
                    key={u.id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/users/${u.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.lastName || '–'}</div>
                      {u.twoFactorEnabled && (
                        <span className="text-xs text-green-600">2FA</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-900">{u.firstName || '–'}</td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{u.email || '–'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded ${ROLE_COLORS[u.role]}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {u.isActive !== false ? (
                        <span className="px-2 py-1 text-xs rounded bg-green-50 text-green-700">Aktiv</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-red-50 text-red-700">Inaktiv</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {u.rfidPrintedCode ? (
                        <span className="text-xs font-mono bg-green-50 text-green-700 px-2 py-1 rounded">
                          {u.rfidPrintedCode}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {u.nfcChipId ? (
                        <Tooltip content={u.nfcChipId} side="top">
                          <span className="text-xs font-mono bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block max-w-[100px] truncate">
                            {u.nfcChipId}
                          </span>
                        </Tooltip>
                      ) : (
                        <span className="text-xs text-slate-400">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); openRfidDialog(u); }}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="RFID zuweisen"
                        >
                          <CreditCard className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleUserStatus(u); }}
                          className={`p-1.5 rounded ${u.isActive !== false 
                            ? 'text-slate-400 hover:text-amber-600 hover:bg-amber-50' 
                            : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`}
                          title={u.isActive !== false ? 'Deaktivieren' : 'Aktivieren'}
                        >
                          {u.isActive !== false ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                        <ChevronRight className="w-4 h-4 text-slate-400 ml-1" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* RFID Dialog */}
      {rfidDialogOpen && rfidUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">RFID zuweisen</h2>
              <button 
                onClick={() => setRfidDialogOpen(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-sm text-slate-600">
                RFID-Chip für <strong>{rfidUser.displayName}</strong> zuweisen
              </p>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Chip-ID (NFC)
                </label>
                <input
                  type="text"
                  value={rfidForm.nfcChipId}
                  onChange={(e) => setRfidForm({ ...rfidForm, nfcChipId: e.target.value })}
                  placeholder="z.B. 04:A3:12:..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Chipnummer
                </label>
                <input
                  type="text"
                  value={rfidForm.rfidPrintedCode}
                  onChange={(e) => setRfidForm({ ...rfidForm, rfidPrintedCode: e.target.value })}
                  placeholder="z.B. 12345678"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {rfidError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {rfidError}
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setRfidDialogOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={saveRfid}
                disabled={savingRfid}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {savingRfid ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      {importDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Benutzer importieren</h2>
              <button
                onClick={() => setImportDialogOpen(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <CsvDropzone
                fields={importFields}
                onImport={handleImport}
                onClose={() => setImportDialogOpen(false)}
                title=""
                description="CSV oder Excel-Datei mit Benutzerdaten hochladen. Neue Benutzer werden als WORKER angelegt."
                showNameSplit={true}
              />
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
}
